import { apiGet } from './apiClient.js';

/**
 * Capability Service
 * 
 * Provides capability information for the current admin user.
 * Uses versioned in-memory cache (per page load) to avoid repeated API calls.
 * Cache is versioned by buildStamp + actorId and expires quickly.
 * Never caches empty capability set for super admin.
 */

let cachedMe = null;
let cacheMetadata = {
  buildStamp: null,
  actorId: null,
  timestamp: null,
  expiresAt: null,
};
let loadingPromise = null;

// Cache TTL: 30 seconds (short to allow quick recovery)
const CACHE_TTL_MS = 30 * 1000;

/**
 * Get current admin user information and capabilities
 * @param {boolean} forceRefresh - If true, bypass cache and fetch fresh data
 * @returns {Promise<object>} User info with capabilities
 */
export async function getAdminMe(forceRefresh = false) {
  // If force refresh, clear cache first
  if (forceRefresh) {
    cachedMe = null;
    cacheMetadata = { buildStamp: null, actorId: null, timestamp: null, expiresAt: null };
    loadingPromise = null;
  }

  // Check cache validity (version + expiration)
  const now = Date.now();
  if (cachedMe && !forceRefresh) {
    const isExpired = cacheMetadata.expiresAt && now > cacheMetadata.expiresAt;
    const isVersionMatch = cacheMetadata.buildStamp && cacheMetadata.actorId &&
      cachedMe.buildStamp === cacheMetadata.buildStamp &&
      cachedMe.actorId === cacheMetadata.actorId;
    
    // If cache is valid and not expired, return it
    if (!isExpired && isVersionMatch) {
      // GUARDRAIL: Never return cached empty capabilities for super admin
      if (cachedMe.isSuperAdmin && (!cachedMe.capabilities || cachedMe.capabilities.length === 0)) {
        console.warn('[RBAC] Cached empty capabilities for super admin - forcing refresh');
        cachedMe = null;
        cacheMetadata = { buildStamp: null, actorId: null, timestamp: null, expiresAt: null };
      } else {
        return cachedMe;
      }
    } else if (isExpired) {
      // Cache expired, clear it
      cachedMe = null;
      cacheMetadata = { buildStamp: null, actorId: null, timestamp: null, expiresAt: null };
    }
  }

  // If already loading, return the existing promise
  if (loadingPromise && !forceRefresh) {
    return loadingPromise;
  }

  // Fetch user info and capabilities
  loadingPromise = apiGet('/admin/me')
    .then((data) => {
      // `apiClient` contract: returns the unwrapped payload for admin endpoints
      const normalizedData = data;
      
      // Ensure actorId is present
      if (!normalizedData.actorId && normalizedData.id) {
        normalizedData.actorId = normalizedData.id;
      }
      
      // GUARDRAIL: Never cache empty capabilities for super admin
      if (normalizedData.isSuperAdmin && (!normalizedData.capabilities || normalizedData.capabilities.length === 0)) {
        console.error('[RBAC] Super admin has empty capabilities - not caching, forcing refresh');
        loadingPromise = null;
        // Force immediate refresh
        return getAdminMe(true);
      }
      
      // Cache with versioning and expiration
      cachedMe = normalizedData;
      cacheMetadata = {
        buildStamp: normalizedData.buildStamp || 'unknown',
        actorId: normalizedData.actorId || 'unknown',
        timestamp: now,
        expiresAt: now + CACHE_TTL_MS,
      };
      
      loadingPromise = null;
      return normalizedData;
    })
    .catch((error) => {
      loadingPromise = null;
      // Attach status code to error for 401 vs 403 detection
      if (error.status) {
        error.isUnauthenticated = error.status === 401 || error.code === 'UNAUTHENTICATED';
        error.isUnauthorized = error.status === 403 || error.code === 'UNAUTHORIZED';
      }
      throw error;
    });

  return loadingPromise;
}

/**
 * Clear the cache (useful for testing or logout)
 */
export function clearCapabilityCache() {
  cachedMe = null;
  loadingPromise = null;
}

